import {
  Button,
  Flashbar,
  FormField,
  Grid,
  Header,
  Input,
  Container,
  SpaceBetween,
  Link,
  Box,
  ProgressBar,
  ContentLayout,
  Select,
  SelectProps,
  Toggle,
} from '@amzn/awsui-components-react';
import React, { useState } from 'react';
import { ESEC2 } from '../../models/types';
import { CodeViewer } from '../../Components/Common/CodeViewer';
import { useExecuteCommandMutation } from './hooks/useExecuteCommandMutation';
import { useApprovalSystem } from './hooks/useApprovalSystem';
import { formatTime } from './utils';
import { API_OPTIONS } from "./constants";

interface CommandExplorerProps {
  domainIdentifier: string;
  instances: ESEC2[];
}

export const CommandExplorer: React.FC<CommandExplorerProps> = (props) => {
  const [executeCommand] = useExecuteCommandMutation();
  const {
    loading,
    error,
    setError,
    approvalState,
    currentRequestId,
    setCurrentRequestId,
    isPolling,
    timeRemaining,
    totalTime,
    commandOutput,
    submitApprovalRequest,
    checkApprovalStatus,
    bypassApproval,
    startPolling,
    stopPolling
  } = useApprovalSystem(executeCommand);

  const [isCheckingExistingRequest, setIsCheckingExistingRequest] = useState(false);
  const [manualRequestId, setManualRequestId] = useState('');
  const [approverId, setApproverId] = useState('');
  const [selectedInstance, setSelectedInstance] = useState<SelectProps.Option | null>(null);
  const [selectedAPI, setSelectedAPI] = useState<SelectProps.Option | null>(null);

  const instanceOptions = props.instances.map(instance => ({
    label: `${instance.instanceId} (${instance.nodeType}) - ${instance.network.privateIp}`,
    value: instance.instanceId,
    description: `Type: ${instance.instanceType}, AZ: ${instance.az}, State: ${instance.state}`,
    nodeType: instance.nodeType,
    privateIp: instance.network.privateIp
  }));

  const handleSubmit = async () => {
    if (isCheckingExistingRequest) {
      await checkApprovalStatus(
        selectedAPI,
        selectedInstance,
        props.domainIdentifier,
        manualRequestId,
        manualRequestId
      );
    } else {
      await submitApprovalRequest(
        selectedAPI,
        selectedInstance,
        props.domainIdentifier,
        approverId
      );
    }
  };

  const handleBypass = async () => {
    await bypassApproval(
      selectedAPI,
      selectedInstance,
      props.domainIdentifier
    );
  };

  const handleInstanceChange = ({ detail }: { detail: { selectedOption: SelectProps.Option } }) => {
    setIsCheckingExistingRequest(false);
    setManualRequestId('');
    setSelectedInstance(detail.selectedOption);
    setCurrentRequestId(null);
    stopPolling();
  };

  const handleAPIChange = ({ detail }: { detail: { selectedOption: SelectProps.Option } }) => {
    setIsCheckingExistingRequest(false);
    setManualRequestId('');
    setSelectedAPI(detail.selectedOption);
    setCurrentRequestId(null);
    stopPolling();
  };

  const renderApprovalMessage = () => {
    if (!approvalState.message) return null;

    let type: 'success' | 'error' | 'info' | 'warning';
    switch (approvalState.status) {
      case 'Approved':
        type = 'success';
        break;
      case 'ApprovalDenied':
      case 'ApprovalInternalError':
        type = 'error';
        break;
      case 'ApprovalResponseExpired':
      case 'ApprovalRequestExpired':
        type = 'warning';
        break;
      default:
        type = 'info';
    }

    return (
      <Flashbar
        items={[
          {
            type,
            content: (
              <div>
                {approvalState.message}
                {approvalState.isBypassable && (
                  <Box margin={{ top: 'xs' }}>
                    <Button onClick={handleBypass} variant="link">
                      Bypass Approval
                    </Button>
                  </Box>
                )}
              </div>
            ),
            dismissible: false
          }
        ]}
      />
    );
  };

  const renderApprovalStatus = () => {
    if (approvalState.status === 'ApprovalPending' && timeRemaining !== null && totalTime !== null) {
      return (
        <Box margin={{ bottom: 'l' }}>
          <SpaceBetween direction="vertical" size="s">
            <div>
              <strong>Approval Request Status:</strong>
              <Box margin={{ top: 'xs' }}>
                Time remaining: {formatTime(timeRemaining)}
              </Box>
              {approvalState.approvalUrl && (
                <Box margin={{ top: 'xs' }}>
                  Approval URL: <Link href={approvalState.approvalUrl} external>
                  Click here to approve
                </Link>
                </Box>
              )}
            </div>
            <ProgressBar
              value={((totalTime - timeRemaining) / totalTime) * 100}
              label="Time elapsed"
              constraintText={`${formatTime(timeRemaining)} remaining`}
              statusType={
                timeRemaining < 300 ? 'error' :
                  timeRemaining < 900 ? 'warning' : 'info'
              }
            />
          </SpaceBetween>
        </Box>
      );
    }
    return null;
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="Execute and monitor commands with approval workflow"
        >
          Command Explorer: {props.domainIdentifier}
        </Header>
      }
    >
      <Container>
        <SpaceBetween direction="vertical" size="l">
          {error && (
            <Flashbar
              items={[
                {
                  type: 'error',
                  content: error,
                  dismissible: true,
                  onDismiss: () => setError('')
                }
              ]}
            />
          )}

          {renderApprovalMessage()}
          {renderApprovalStatus()}

          <Box padding="l">
            <Grid
              gridDefinition={[
                { colspan: { default: 12 } },
                { colspan: { default: 12 } },
                { colspan: { default: 12, m: 6 } },
                { colspan: { default: 12, m: 6 } },
                { colspan: { default: 12 } },
              ]}
            >
              <FormField
                label={<strong>Select Command</strong>}
                constraintText="Select the command to execute"
                errorText={!selectedAPI ? 'Command selection is required' : undefined}
              >
                <Select
                  selectedOption={selectedAPI}
                  onChange={handleAPIChange}
                  options={API_OPTIONS}
                  filteringType="auto"
                  placeholder="Choose a command"
                  expandToViewport
                />
              </FormField>

              <FormField
                label={<strong>Select Instance</strong>}
                constraintText="Select an instance to execute commands on"
                errorText={!selectedInstance ? 'Instance selection is required' : undefined}
              >
                <Select
                  selectedOption={selectedInstance}
                  onChange={handleInstanceChange}
                  options={instanceOptions}
                  filteringType="auto"
                  placeholder="Choose an instance"
                />
              </FormField>

              <SpaceBetween direction="vertical" size="xs">
                <FormField
                  label={<strong>Request ID</strong>}
                  description="Enter an existing request ID to check its status, or submit a new request"
                >
                  <SpaceBetween direction="horizontal" size="xs">
                    <Input
                      value={isCheckingExistingRequest ? manualRequestId : (currentRequestId || 'Not generated yet')}
                      onChange={({ detail }) => setManualRequestId(detail.value)}
                      placeholder={isCheckingExistingRequest ? "Enter request ID" : "Request ID will be generated"}
                      readOnly={!isCheckingExistingRequest}
                    />
                    {currentRequestId && !isCheckingExistingRequest && (
                      <Button
                        iconName="copy"
                        variant="normal"
                        onClick={() => {
                          navigator.clipboard.writeText(currentRequestId);
                          setError('Request ID copied to clipboard!');
                          setTimeout(() => setError(''), 2000);
                        }}
                      >
                        Copy
                      </Button>
                    )}
                  </SpaceBetween>
                </FormField>

                <Toggle
                  onChange={({ detail }) => {
                    setIsCheckingExistingRequest(detail.checked);
                    if (!detail.checked) {
                      setManualRequestId('');
                    }
                  }}
                  checked={isCheckingExistingRequest}
                >
                  Check existing request
                </Toggle>
              </SpaceBetween>

              {!isCheckingExistingRequest && (
                <FormField
                  label={<strong>Approver ID</strong>}
                  constraintText={
                    approvalState.approverGroups
                      ? `Approver must be in groups: ${approvalState.approverGroups.join(', ')}`
                      : "Enter the ID of the approver who will review this command execution"
                  }
                  errorText={!approverId.trim() ? 'Approver ID is required' : undefined}
                >
                  <Input
                    onChange={({ detail }) => setApproverId(detail.value)}
                    value={approverId}
                    placeholder="Enter approver ID"
                  />
                </FormField>
              )}

              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  loading={loading || isPolling}
                  disabled={
                    !selectedAPI || !selectedInstance || (
                      isCheckingExistingRequest
                        ? !manualRequestId.trim()
                        : (!approverId.trim() || approvalState.status === 'ApprovalPending')
                    )
                  }
                >
                  {isCheckingExistingRequest
                    ? 'Check Request Status'
                    : (approvalState.status === 'none' || approvalState.status === 'ApprovalRequestExpired' ||
                      approvalState.status === 'ApprovalResponseExpired' || approvalState.status === 'ApprovalDenied'
                        ? 'Submit for Approval'
                        : 'Execute Command'
                    )
                  }
                </Button>
              </div>
            </Grid>

            {commandOutput && (
              <Box padding={{ top: 'l' }}>
                <Header variant="h2">Command Output</Header>
                <CodeViewer
                  codeData={commandOutput}
                  language={{ label: 'json', value: 'json' }}
                  theme={{ label: 'coy', value: 'coy' }}
                />
              </Box>
            )}
          </Box>
        </SpaceBetween>
      </Container>
    </ContentLayout>
  );
};
